import "./App.scss";
import "react-toastify/dist/ReactToastify.css";
import {NavLink, Outlet} from "react-router";
import React, {useContext, useMemo} from "react";
import useApp from "./useApp";
import {ToastContainer} from "react-toastify";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {rxShowAccountEditor, selectAllAccounts} from "./slices/accountsSlice";
import useFormat from "./util/useFormat";
import {AccountEditor} from "./screens/accounts/account-editor/AccountEditor";
import {MODE_CREATE, MODE_EDIT} from "./codes";
import usePortfolioApp from "./screens/portfolio/list/usePortfolioApp";
import {rxShowCarEditor, selectAllCars} from "./slices/carsSlice";
import {selectAllMileageLogs} from "./slices/mileageLogsSlice";
import {CarEditor} from "./screens/cars/car-editor/CarEditor";
import useProject from "./util/useProject";
import {appConfigSelector} from "./slices/appSlice";
import {ExpiredModal} from "./ExpiredModal";
import {NetworkErrorModal} from "./common/AppModal";
import useRate from "./util/useRate";
import clsx from "clsx";
import useBudgetApp from "./screens/budgets/useBudgetApp";
import moment from "moment";
import {BudgetBarIn, BudgetBarOut} from "./common/BudgetBar";
import { rxShowLoanEditor, selectAllLoans } from "./slices/loansSlice";

export const AppContext = React.createContext({});

function App() {
  const {
    toggleLeftMenu,
    closeLeftMenuMobile,
    logout
  } = useApp();

  const menuState = useSelector(state => state.app.menuState);
  const initialized = useSelector(state => state.app.initialized);
  const token = useSelector(state => state.app.token);

  const {quotes, marketValue, initialValue, dayGains, ongoingGains} = usePortfolioApp();
  const {periods} = useBudgetApp();

  const {t} = useTranslation("app");

  const providerValue = useMemo(() => ({
    portfolio: {
      quotes,
      marketValue,
      initialValue,
      dayGains,
      ongoingGains
    },
    budget: {
      periods
    }
  }), [quotes, marketValue, initialValue, dayGains, ongoingGains, periods]);

  if (!initialized) {
    return null;
  }

  return (
    <AppContext.Provider value={providerValue}>
      <div id="app" className={menuState ? "menu-state-1" : "menu-state-2"}>
        <div id="top-border">
          <div id="content-rounded-border">
            <div/>
          </div>
        </div>

        <div id="left-menu" className={clsx("custom-scrollbar")}>
          <Logo />

          <NavLink to="/" end className="menu-entry" onClick={closeLeftMenuMobile}>
            <span className="menu-icon"><span className="material-icons">home</span></span>
            <span className="menu-name">{t("menu_home")}</span>
          </NavLink>

          <MenuBudgets onNavigate={closeLeftMenuMobile} periods={periods}/>
          <MenuAccounts onNavigate={closeLeftMenuMobile}/>
          <MenuPortfolio onNavigate={closeLeftMenuMobile}/>
          <MenuCars onNavigate={closeLeftMenuMobile}/>
          <MenuLoans onNavigate={closeLeftMenuMobile}/>

          <div id="menu-separator"/>

          <div className="menu-divider"/>

          <NavLink to="/settings" className="menu-entry" onClick={closeLeftMenuMobile}>
            <span className="menu-icon"><span className="material-icons">settings</span></span>
            <span className="menu-name">{t("menu_settings")}</span>
          </NavLink>

          <NavLink to="/support" className="menu-entry" onClick={closeLeftMenuMobile}>
            <span className="menu-icon"><span className="material-icons">help_outline</span></span>
            <span className="menu-name">{t("menu_support")}</span>
          </NavLink>

          <div className="menu-entry logout" onClick={logout}>
            <span className="menu-icon"><span className="material-icons">logout</span></span>
            <span className="menu-name">
              {t("menu_logout")}
              <br/>
              <span className="email">{token.email}</span>
            </span>
          </div>
        </div>

        <div id="content">
          <Outlet/>

          <AccountEditor/>
          <CarEditor/>

          <ExpiredModal/>
          <NetworkErrorModal/>

          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </div>

        <div id="left-menu-overlay" className={menuState ? "state-1" : "state-2"} onClick={toggleLeftMenu}/>
      </div>
    </AppContext.Provider>
  );
}

const Logo = () => {
  const {formatAmount} = useFormat();
  const {getPrice} = useRate();
  const {marketValue} = useContext(AppContext).portfolio;
  const {currencyMain, portfolioCurrency, featurePortfolioActive} = useSelector(appConfigSelector);

  const accounts = useSelector(selectAllAccounts);
  const activeAccounts = accounts.filter(a => a.active);

  let total = activeAccounts
    .map(a => getPrice(a.balance, a.currency, currencyMain))
    .reduce((a, b) => a + b, 0);

  if (featurePortfolioActive) {
    total += getPrice(marketValue, portfolioCurrency, currencyMain);
  }

  return <div id="app-logo">
    <img src="/android-chrome-192x192.png" alt="Logo"/>

    <div id="app-name">Jikiwi</div>
    <div id="total-balance">{formatAmount(total)}</div>
  </div>;
}

const MenuBudgets = ({onNavigate, periods}) => {
  const {t} = useTranslation("app");

  const now = moment();
  const currentYear = now.year();
  const currentMonth = now.month();
  const currentPeriod = periods.find(p => p.year === currentYear && p.month === currentMonth);

  return <>
    <NavLink to="/budgets" end className="menu-entry" onClick={onNavigate}>
      <span className="menu-icon"><span className="material-icons">article</span></span>
      <span className="menu-name">{t("menu_budgets")}</span>
    </NavLink>

    <div className="budgetBars">
      <div className="budgetBarContainer">
        <BudgetBarIn amountFilled={currentPeriod.amountInFilled}
                     amountPlanned={currentPeriod.amountInPlanned}
                     background="#ffffff22"/>
      </div>

      <div className="budgetBarContainer">
        <BudgetBarOut amountFilled={currentPeriod.amountOutFilled}
                      amountPlanned={currentPeriod.amountOutPlanned}
                      background="#ffffff22"/>
      </div>
    </div>
  </>;
};

const MenuAccounts = ({onNavigate}) => {
  const {formatAmount} = useFormat();
  const {getPrice} = useRate();
  const {t} = useTranslation("app");

  const dispatch = useDispatch();
  const accounts = useSelector(selectAllAccounts);
  const {currencyMain} = useSelector(appConfigSelector);

  const {runWithExpiration} = useProject();

  const activeAccounts = accounts.filter(a => a.active);

  const showAccountEditor = (mode, account) => dispatch(rxShowAccountEditor({mode, account}));

  return <>
    <NavLink to="/transactions" end className="menu-entry" onClick={onNavigate}>
      <span className="menu-icon"><span className="material-icons">account_balance</span></span>
      <span className="menu-name">{t("menu_accounts")}</span>
      <span className="menu-right">
        {formatAmount(
          activeAccounts
            .map(a => getPrice(a.balance, a.currency, currencyMain))
            .reduce((a, b) => a + b, 0)
        )}
      </span>
    </NavLink>

    <div className="menu-entry-accounts">
      {activeAccounts.length === 0 &&
        <div className="menu-add-entry">
          <span onClick={runWithExpiration(() => showAccountEditor(MODE_CREATE, {}))}>
            Ajouter un compte
          </span>
        </div>
      }

      {activeAccounts.map(account => {
        const [background] = account.color.split("|");

        return <div className="menu-entry-account" key={account.id}>
          <span
            className="account-edit material-icons"
            onClick={runWithExpiration(() => showAccountEditor(MODE_EDIT, account))}
          >
            edit
          </span>
          <div className="account-color" style={{background}}/>
          <div className="account-name">{account.name}</div>
          <div className="account-balance">{formatAmount(account.balance, account.currency)}</div>
        </div>;
      })}
    </div>
  </>;
};

const MenuPortfolio = ({onNavigate}) => {
  const {formatAmount} = useFormat();
  const {t} = useTranslation("app");

  const {marketValue, dayGains, ongoingGains} = useContext(AppContext).portfolio;

  const {featurePortfolioActive, portfolioCurrency} = useSelector(appConfigSelector);

  if (!featurePortfolioActive) {
    return null;
  }

  return <>
    <NavLink to="/portfolio" className="menu-entry" onClick={onNavigate}>
      <span className="menu-icon"><span className="material-icons">trending_up</span></span>
      <span className="menu-name">{t("menu_portfolio")}</span>
      <span className="menu-right">{formatAmount(marketValue, portfolioCurrency)}</span>
    </NavLink>

    <div className="menu-entry-gains">
      <div className="menu-entry-gain">
        <div className="gain-color" style={{background: dayGains >= 0 ? "#30a14e" : "#BE0000"}}/>
        <div className="gain-name">{t("day_gains")}</div>
        <div className="gain-balance">{formatAmount(dayGains, portfolioCurrency)}</div>
      </div>
      <div className="menu-entry-gain">
        <div className="gain-color" style={{background: ongoingGains >= 0 ? "#30a14e" : "#BE0000"}}/>
        <div className="gain-name">{t("total_gains")}</div>
        <div className="gain-balance">{formatAmount(ongoingGains, portfolioCurrency)}</div>
      </div>
    </div>
  </>;
};

const MenuCars = ({onNavigate}) => {
  const {t} = useTranslation("app");
  const {formatNumber} = useFormat();

  const dispatch = useDispatch();
  const cars = useSelector(selectAllCars);
  const mileageLogs = useSelector(selectAllMileageLogs);

  const {runWithExpiration} = useProject();

  const {featureCarsActive} = useSelector(appConfigSelector);

  if (!featureCarsActive) {
    return null;
  }

  const activeCars = cars.filter(c => c.active);

  const showCarEditor = (mode, car) => dispatch(rxShowCarEditor({mode, car}));

  return <>
    <NavLink to="/cars" end className="menu-entry" onClick={onNavigate}>
      <span className="menu-icon"><span className="material-icons">local_gas_station</span></span>
      <span className="menu-name">{t("menu_cars")}</span>
    </NavLink>

    <div className="menu-entry-cars">
      {activeCars.length === 0 &&
        <div className="menu-add-entry">
          <span onClick={runWithExpiration(() => showCarEditor(MODE_CREATE, {}))}>
            Ajouter une voiture
          </span>
        </div>
      }

      {activeCars.map(car => {
        const [background] = car.color.split("|");

        let averageConso = null;

        const carMileageLogs = mileageLogs.filter(ml => ml.car === car.id);

        if (carMileageLogs.length > 1) {
          const lastMileageLog = carMileageLogs[0];
          const firstMileageLog = carMileageLogs[carMileageLogs.length - 1];

          const fullQuantity = carMileageLogs.map(ml => ml.quantity).reduce((a, b) => a + b, 0) - firstMileageLog.quantity;
          const fullDistance = lastMileageLog.odometer - firstMileageLog.odometer;

          averageConso = fullQuantity / fullDistance * 100;
        }

        return <div className="menu-entry-car" key={car.id}>
          <span
            className="car-edit material-icons"
            onClick={runWithExpiration(() => showCarEditor(MODE_EDIT, car))}
          >
            edit
          </span>
          <div className="car-color" style={{background}}/>
          <div className="car-name">{car.name}</div>
          <div className="car-conso">
            {averageConso ? formatNumber(averageConso, "#T##0D000") : "-"} {car.consoUnit}
          </div>
        </div>;
      })}
    </div>
  </>;
};

const MenuLoans = ({onNavigate}) => {
  const {t} = useTranslation("app");

  const {formatNumber} = useFormat();

  const dispatch = useDispatch();
  const loans = useSelector(selectAllLoans);

  const {runWithExpiration} = useProject();

  const showLoanEditor = (mode, loan) => dispatch(rxShowLoanEditor({mode, loan}));

  const allLoansTotalAmount = loans.reduce((a, loan) => a + (loan.premium * loan.duration), 0);
  const allLoansReimbursed = loans.reduce((a, loan) => a + loan.installments.reduce((a, b) => a + b.amount, 0), 0);

  return <>
    <NavLink to="/loans" end className="menu-entry" onClick={onNavigate}>
      <span className="menu-icon"><span className="material-icons">real_estate_agent</span></span>
      <span className="menu-name">{t("Emprunts")} ({loans.length})</span>
      <span className="menu-right">{formatNumber(allLoansReimbursed / allLoansTotalAmount * 100)} %</span>
    </NavLink>

    <div className="budgetBars">
      <div className="budgetBarContainer">
        <BudgetBarIn amountFilled={allLoansReimbursed}
                     amountPlanned={allLoansTotalAmount}
                     background="#ffffff22"/>
      </div>
    </div>

    <div className="menu-entry-loans">
      {loans.length === 0 &&
        <div className="menu-add-entry">
          <span onClick={runWithExpiration(() => showLoanEditor(MODE_CREATE, {}))}>
            Ajouter un prêt
          </span>
        </div>
      }
    </div>
  </>;
};

export default App;
